<template>
    <div :class="['border-2 h-full overflow-y-auto border-opacity-10 rounded-lg py-2', maxHeightClass]">
        <table v-if="nodes.length" class="w-full">
            <thead></thead>
            <tbody>
                <template v-for="node of nodes" :key="node.key">
                    <tr class="hover:bg-gray-100" :class="rowCursor(node)" @click="onNodeClicked(node)">
                        <td class="pl-4 py-2 font-medium" :class="{ 'text-gray-400': isDisabled(node) }">
                            <span v-if="hasExpandColumn" class="inline-flex items-center mr-3">
                                <button v-if="childrenOf(node).length" type="button" class="inline-flex rounded-full p-2.5 hover:bg-gray-200 transition-colors" @click.stop="toggleExpanded(node)">
                                    <ExpandIcon color="black" :expand="isExpanded(node)" class="w-3.5 h-3.5" />
                                </button>
                                <!-- Keeps childless rows' labels aligned with their expandable siblings. -->
                                <span v-else class="inline-flex rounded-full p-2.5">
                                    <span class="inline-block w-3.5 h-3.5"></span>
                                </span>
                            </span>
                            <span>{{ node.label }}</span>
                        </td>
                        <td class="w-6.5 pr-4 py-2">
                            <div class="flex justify-center">
                                <CustomCheckbox tabbable :checked="isChecked(node)" :indeterminate="isIndeterminate(node)" :disabled="isDisabled(node)" @update:checked="onNodeClicked(node)"></CustomCheckbox>
                            </div>
                        </td>
                    </tr>

                    <tr v-if="childrenOf(node).length">
                        <td colspan="2" class="p-0">
                            <ExpandCollapseTransition>
                                <div v-show="isExpanded(node)">
                                    <table class="w-full">
                                        <tbody>
                                            <tr v-for="child of childrenOf(node)" :key="child.key" class="hover:bg-gray-50" :class="rowCursor(child)" @click="onNodeClicked(child)">
                                                <td class="pl-16 py-2 text-sm text-gray-700" :class="{ 'text-gray-400': isDisabled(child) }">{{ child.label }}</td>
                                                <td class="w-6.5 pr-4 py-2">
                                                    <div class="flex justify-center">
                                                        <CustomCheckbox tabbable :checked="isChecked(child)" :disabled="isDisabled(child)" @update:checked="onNodeClicked(child)"></CustomCheckbox>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </ExpandCollapseTransition>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>

        <p v-else-if="emptyLabel" class="py-8 text-center text-sm text-dark-gray-600">{{ emptyLabel }}</p>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import ExpandIcon from '@/components/icons/ExpandIcon.vue';
import { CheckboxTreeNode } from '@/types';

/**
 * A bordered, scrollable list of checkbox rows, optionally one level deep, with tri-state parents.
 *
 * Only leaf keys are ever selected. A parent's checkbox is derived from its children - checked when
 * they all are, indeterminate when only some are - so a caller never has to keep parents and children
 * in sync, and a half-filled parent reads as half-filled rather than as untouched.
 *
 * Selection and expansion are both passed in and emitted back rather than held here, so the caller
 * stays the single owner of its own state. That is what lets the month picker sort its selection
 * chronologically and open the newest year on arrival without this component knowing about either.
 *
 * Renders two levels, which is what every list it replaces needs. Arbitrary depth would want the
 * recursive self-rendering node ContentOptionsNode.vue already does; the selection logic below is
 * already depth-agnostic, only the markup is not.
 */
export default defineComponent({
    components: { CustomCheckbox, ExpandCollapseTransition, ExpandIcon },
    props: {
        nodes: {
            type: Array as PropType<CheckboxTreeNode[]>,
            required: true,
        },
        /** Leaf keys only; parent keys are derived and never stored. */
        selectedKeys: {
            type: Array as PropType<string[]>,
            required: true,
        },
        expandedKeys: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        /** Shown instead of the table when there is nothing to list. Omit to render nothing. */
        emptyLabel: {
            type: String,
            default: '',
        },
        /** Named to match ContentOptionsSelector's prop, which wants a shorter box than the default. */
        maxHeightClass: {
            type: String,
            default: 'max-h-[50rem]',
        },
    },
    emits: ['update:selectedKeys', 'update:expandedKeys'],
    computed: {
        /**
         * A flat list needs no expander and no alignment spacer, so it comes out as the plain rows it
         * was before rather than as a tree that happens to have no branches.
         */
        hasExpandColumn(): boolean {
            return this.nodes.some((node) => (node.children ?? []).length > 0);
        },
        selectedSet(): Set<string> {
            return new Set(this.selectedKeys);
        },
    },
    methods: {
        childrenOf(node: CheckboxTreeNode): CheckboxTreeNode[] {
            return node.children ?? [];
        },
        /** Every selectable key under a node - the node itself when it is a leaf. */
        leafKeys(node: CheckboxTreeNode): string[] {
            const children = this.childrenOf(node);
            if (!children.length) return [node.key];

            return children.flatMap((child) => this.leafKeys(child));
        },
        /** The leaves under a node that may still be ticked. */
        selectableLeaves(node: CheckboxTreeNode): CheckboxTreeNode[] {
            const children = this.childrenOf(node);
            if (!children.length) return node.disabled ? [] : [node];

            return children.flatMap((child) => this.selectableLeaves(child));
        },
        isDisabled(node: CheckboxTreeNode): boolean {
            // A parent is only ever as disabled as the children it rolls up.
            return this.childrenOf(node).length ? false : !!node.disabled;
        },
        isExpanded(node: CheckboxTreeNode): boolean {
            return this.expandedKeys.includes(node.key);
        },
        isChecked(node: CheckboxTreeNode): boolean {
            const keys = this.leafKeys(node);
            return keys.length > 0 && keys.every((key) => this.selectedSet.has(key));
        },
        isIndeterminate(node: CheckboxTreeNode): boolean {
            const keys = this.leafKeys(node);
            const selected = keys.filter((key) => this.selectedSet.has(key)).length;
            return selected > 0 && selected < keys.length;
        },
        rowCursor(node: CheckboxTreeNode): string {
            return this.isDisabled(node) ? 'cursor-not-allowed' : 'cursor-pointer';
        },
        toggleExpanded(node: CheckboxTreeNode) {
            const next = this.isExpanded(node) ? this.expandedKeys.filter((key) => key !== node.key) : [...this.expandedKeys, node.key];

            this.$emit('update:expandedKeys', next);
        },
        onNodeClicked(node: CheckboxTreeNode) {
            if (this.isDisabled(node)) return;

            // Indeterminate counts as "not all selected", so the first click on a half-filled parent
            // fills it rather than clearing the leaves the user just picked.
            if (this.isChecked(node)) {
                const removed = new Set(this.leafKeys(node));
                this.$emit(
                    'update:selectedKeys',
                    this.selectedKeys.filter((key) => !removed.has(key)),
                );
                return;
            }

            // Appended rather than merged in node order, so a caller that cares about ordering sorts it
            // once on the way in and every other caller keeps the order the user clicked in.
            const added = this.selectableLeaves(node)
                .map((leaf) => leaf.key)
                .filter((key) => !this.selectedSet.has(key));

            this.$emit('update:selectedKeys', [...this.selectedKeys, ...added]);
        },
    },
});
</script>
